import { gql, useMutation } from "@apollo/client";
import {
  Alignment,
  Button,
  Classes,
  Navbar,
  NavbarDivider,
  NavbarGroup,
  Position,
  Toaster,
} from "@blueprintjs/core";
import React from "react";
import { Link } from "react-router-dom";
import { AuthWrapper } from "../components/AuthWrapper";
import { IMenuItem, NavbarMenu } from "../components/NavbarMenu";
import { Settings } from "../components/Settings";
import { Spinner } from "../components/Spinner";
import { ApplicationName } from "../config";
import { useGlobalState } from "../Context";
import "../css/template.css";
import { UserLanguage } from "../lang/lang";
import { useApiUser } from "../store/Me";
import { LoginView } from "./auth/LoginView";

const LogoutMutation = gql`
  mutation logout {
    logout
  }
`;

interface TemplateProps {
  children: React.ReactNode | JSX.Element;
}

interface GridTemplateProps {
  children: React.ReactNode | JSX.Element;
}

const MenuItems: IMenuItem[] = [
  {
    text: UserLanguage().data.article.label,
    left_icon: "barcode",
    children: [
      {
        text: UserLanguage().pages.articleListView.label,
        to: "/articles/view",
        left_icon: "panel-table",
      },
      {
        text: UserLanguage().pages.articleCategory.list,
        to: "/articles/categories",
        left_icon: "data-lineage",
      },
      {
        text: UserLanguage().pages.productGroup.list,
        to: "/product-groups",
        left_icon: "data-lineage",
      },
      {
        text: UserLanguage().pages.article.list,
        to: "/articles/all",
      },
    ],
  },
  {
    text: "Data",
    left_icon: "database",
    children: [
      {
        text: UserLanguage().pages.footprint.list,
        to: "/footprints",
      },
      {
        text: UserLanguage().pages.location.list,
        to: "/locations",
      },
    ],
  },
  {
    text: UserLanguage().menu.supply.root_label,
    left_icon: "truck",
    children: [
      {
        text: UserLanguage().menu.supply.distributors,
        to: "/distributors",
        left_icon: "truck",
      },
      {
        text: UserLanguage().menu.supply.article_sources,
        to: "/articles/sources",
        left_icon: "list",
      },
    ],
  },
  {
    text: UserLanguage().pages.customer.menu_label,
    left_icon: "user",
    children: [
      {
        text: UserLanguage().pages.customer.list.label,
        to: "/customers",
      },
    ],
  },
  {
    text: UserLanguage().pages.customerOrder.list,
    to: "/customer/orders",
  },
  {
    text: UserLanguage().pages.userlist.label,
    to: "/users",
  },
];

export const GridTemplate = (props: GridTemplateProps) => {
  const { set_global_state, global_state } = useGlobalState();
  const { profile } = useApiUser();
  const [logout_mutation] = useMutation(LogoutMutation);

  const logout = () =>
    logout_mutation()
      .then(() => window.location.reload())
      .catch((e) => console.log(e));

  const bg_classes = global_state.dark_mode ? "bg container-dark" : "bg";
  const container_classes = global_state.dark_mode
    ? "container container-dark bp4-dark"
    : "container";

  return (
    <div className={container_classes}>
      <div id="bg" className={bg_classes} />
      <div className="nav_container">
        <Navbar className={Classes.DARK}>
          <NavbarMenu items={MenuItems}>
            <Link to="/" style={{ color: "white", textDecoration: "none" }}>
              <Button minimal={true} text={ApplicationName} />
            </Link>
          </NavbarMenu>
          <NavbarGroup align={Alignment.RIGHT}>
            <Link to="/me" style={{ color: "white", textDecoration: "none" }}>
              <Button minimal text={UserLanguage().pages.profile.title} />
            </Link>
            {profile.user ? (
              <Button
                text={UserLanguage().actions.logout}
                minimal
                onClick={logout}
              />
            ) : null}
            <NavbarDivider />
            <Settings
              is_dark_mode={global_state.dark_mode}
              set_dark_mode={(b) => set_global_state({ dark_mode: b })}
            />
          </NavbarGroup>
        </Navbar>
      </div>
      <div className="content_container">{props.children}</div>
    </div>
  );
};

export const AppToaster = Toaster.create({
  position: Position.TOP,
});

export const Template = (props: TemplateProps) => (
  <GridTemplate>
    <AuthWrapper
      auth={props.children}
      noAuth={<LoginView />}
      loading={<Spinner />}
    />
  </GridTemplate>
);
